import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';

interface QuotaExhaustedModalProps {
  isOpen: boolean;
  onClose?: () => void;
  usedCount: number;
  totalCount: number;
  planName?: string;
  blocking?: boolean;
  userTimezone?: string;
}

export const QuotaExhaustedModal: React.FC<QuotaExhaustedModalProps> = ({
  isOpen,
  onClose,
  usedCount,
  totalCount,
  planName = 'Anonymous',
  blocking = false,
  userTimezone = 'UTC'
}) => {
  const pmEnabled = useFeatureFlag('PM');
  const [countdown, setCountdown] = useState<string>('');

  // Calculate time until daily reset (midnight in user's timezone)
  const getTimeUntilReset = () => {
    const now = new Date();

    // Get current time in user's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(now);
    const getPartValue = (type: string) => parts.find(p => p.type === type)?.value || '0';

    const hour = parseInt(getPartValue('hour'));
    const minute = parseInt(getPartValue('minute'));
    const second = parseInt(getPartValue('second'));

    // Calculate seconds until midnight in user's timezone
    const secondsUntilMidnight = (24 - hour - 1) * 3600 + (60 - minute - 1) * 60 + (60 - second);

    const hours = Math.floor(secondsUntilMidnight / 3600);
    const minutes = Math.floor((secondsUntilMidnight % 3600) / 60);

    return `${hours}h ${minutes}m`;
  };

  // Get timezone abbreviation for display
  const getTimezoneAbbr = () => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(new Date());
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      return tzPart?.value || userTimezone;
    } catch (error) {
      console.error('[QuotaExhaustedModal] Error getting timezone abbr:', error);
      return userTimezone;
    }
  };

  // Update countdown every minute
  useEffect(() => {
    if (!isOpen) return;

    const updateCountdown = () => {
      setCountdown(getTimeUntilReset());
    };

    // Update immediately
    updateCountdown();

    // Update every minute
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [isOpen, userTimezone]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop with blur - positioned below header to keep header accessible */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-[9990]" />

      {/* Modal - centered and non-dismissible, positioned below header */}
      <div
        className="fixed inset-0 z-[9991] flex items-center justify-center p-4"
        onClick={blocking ? undefined : onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quota-modal-title"
      >
        <div
          className="bg-slate-900 border-2 border-red-500/50 rounded-xl shadow-2xl p-8 max-w-2xl w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-red-400 text-5xl mb-3">🚫</div>
          <h3
            id="quota-modal-title"
            className="text-2xl font-bold text-white"
          >
            Daily Limit Reached
          </h3>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <p className="text-slate-300 text-center">
            You've used all <strong className="text-red-300">{usedCount}/{totalCount}</strong> daily comparisons
            on the <strong className="text-red-300">{planName}</strong> plan.
          </p>

          {/* Reset time info with countdown */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <p className="text-slate-300 text-center">
              Your daily limit will reset at midnight {getTimezoneAbbr()}.
            </p>
            {countdown && (
              <p className="text-cyan-400 text-center mt-2 font-medium">
                Resets in: {countdown}
              </p>
            )}
          </div>

          {pmEnabled ? (
            <>
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
                <p className="text-slate-300 text-sm font-medium mb-3">
                  Upgrade to get more comparisons:
                </p>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center justify-between">
                    <span>• Flow Plan</span>
                    <span className="text-cyan-400 font-medium">100/day</span>
                  </li>
                  <li className="flex items-center justify-between">
                    <span>• Nexus Plan</span>
                    <span className="text-cyan-400 font-medium">250/day</span>
                  </li>
                </ul>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col gap-3">
                <Link
                  to="/signup"
                  className="w-full px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-semibold transition-all text-center shadow-lg"
                >
                  Upgrade to Flow Plan →
                </Link>
                <Link
                  to="/signup"
                  className="w-full px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors text-center"
                >
                  View All Plans
                </Link>
                {!blocking && onClose && (
                  <button
                    onClick={onClose}
                    className="w-full px-6 py-2 text-slate-400 hover:text-slate-300 text-sm transition-colors"
                  >
                    Maybe Later
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
      </div>
    </>
  );
};
